import { Injectable } from "@angular/core";
import { ImportStockShortfall } from "../../../shared/types/import-stock-shortfall.types";
import { take } from "rxjs/operators";
import * as XLSX from "xlsx";
import { DataService } from "../data/data.service";
import { NotificationService } from "../notification/notification.service";
import { StateService } from "../state/state.service";
import {
    GetActiveOrderQuery,
} from "../../../common/generated-types";
import { ADD_MULTIPLE_ITEMS_TO_ORDER } from "../../components/cart/cart.graphql";
import { GET_STOREFRONT_CONSOLIDATED_FEED } from "../../../common/graphql/consolidated-feed.graphql";
import { normalizeGtin } from "../../../common/utils/gtin.utils";
import { syncConsolidatedFeedOrderFlagFromActiveOrder } from "../../../common/utils/mark-consolidated-feed-order";

export type CartUploadOptions = {
    allowedPricingIds?: number[];
    consolidatedFeedImport?: boolean;
};

export type CartUploadProduct = {
    gtin: string;
    quantity: number;
    supplierId?: number;
    consolidatedFeedMarker?: boolean;
};

@Injectable({
    providedIn: "root",
})
export class CartUploadService {
    stockShortfalls: ImportStockShortfall[] = [];
    private static readonly GTIN_HEADERS = [
        "ean",
        "gtin",
        "barcode",
        "ean13",
        "ean/upc",
        "ean code",
        "ean_code",
        "upc",
    ];

    private static readonly QUANTITY_HEADERS = [
        "quantity",
        "qty",
        "order",
        "amount",
    ];

    private static readonly SUPPLIER_HEADERS = [
        "supplierid",
        "supplier_id",
        "supplier id",
        "supplier",
        "pricelist",
        "pricelist id",
        "pricelist_id",
        "offer id",
        "offer_id",
        "offerid",
        "offer",
    ];

    private static readonly CONSOLIDATED_FEED_OFFER_MARKERS = new Set([
        "consolidated-feed",
        "consolidated feed",
        "consolidated",
        "cf",
    ]);

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private stateService: StateService,
    ) {}

    async readCartUploadFile(file: File): Promise<CartUploadProduct[]> {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileName = file.name.toLowerCase();
        const prefersExcel =
            this.isExcelBuffer(bytes) ||
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".xls");

        if (prefersExcel) {
            try {
                return this.parseExcelBuffer(bytes);
            } catch (excelError) {
                const csvText = this.decodeTextBuffer(bytes);
                if (this.looksLikeCsvText(csvText)) {
                    return this.parseCsvText(csvText);
                }
                throw excelError;
            }
        }

        try {
            return this.parseCsvText(this.decodeTextBuffer(bytes));
        } catch (csvError) {
            if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
                return this.parseExcelBuffer(bytes);
            }
            throw csvError;
        }
    }

    /** @deprecated Use readCartUploadFile instead */
    async readCsvFile(file: File): Promise<CartUploadProduct[]> {
        return this.readCartUploadFile(file);
    }

    /**
     * Extract EAN/GTIN codes from a CSV or Excel file for catalog search.
     * Uses an EAN/GTIN column when present; otherwise reads codes from the cells.
     * Quantity is not required.
     */
    async readEanCodesFromFile(file: File): Promise<string[]> {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileName = file.name.toLowerCase();
        const prefersExcel =
            this.isExcelBuffer(bytes) ||
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".xls");

        let rows: string[][];

        if (prefersExcel) {
            try {
                return this.readEanCodesFromExcelBuffer(bytes);
            } catch (excelError) {
                const csvText = this.decodeTextBuffer(bytes);
                if (
                    this.looksLikeCsvText(csvText) ||
                    this.looksLikeEanListText(csvText)
                ) {
                    rows = this.readRowsFromCsvText(csvText);
                } else {
                    throw excelError;
                }
            }
        } else {
            try {
                rows = this.readRowsFromCsvText(this.decodeTextBuffer(bytes));
            } catch (csvError) {
                if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
                    return this.readEanCodesFromExcelBuffer(bytes);
                } else {
                    throw csvError;
                }
            }
        }

        return this.parseEanCodesFromRows(rows);
    }

    /**
     * Export cart lines to XLSX for re-upload.
     * Columns: EAN, Name, Quantity, Offer ID (Offer ID = pricing/supplier id).
     */
    exportCartToXlsx(
        lines: Array<{
            quantity: number;
            productVariant?: {
                name?: string | null;
                product?: {
                    customFields?: { gtin?: string | null } | null;
                } | null;
                pricing?: { id?: string | number | null } | null;
            } | null;
        }> | null | undefined,
    ): void {
        const rows = (lines || [])
            .map((line) => {
                const gtin = normalizeGtin(
                    line.productVariant?.product?.customFields?.gtin ?? "",
                );
                const quantity = Number(line.quantity) || 0;
                if (!gtin || gtin === "0" || quantity <= 0) {
                    return null;
                }
                return [
                    gtin,
                    line.productVariant?.name ?? "",
                    quantity,
                    line.productVariant?.pricing?.id != null
                        ? String(line.productVariant.pricing.id)
                        : "",
                ];
            })
            .filter((row): row is string[] => row != null);

        if (rows.length === 0) {
            this.notificationService
                .error("No cart lines available to export.")
                .subscribe();
            return;
        }

        const sheetData = [
            ["EAN", "Name", "Quantity", "Offer ID"],
            ...rows,
        ];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cart");

        const date = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `venduro-cart-${date}.xlsx`);
    }

    private isExcelBuffer(bytes: Uint8Array): boolean {
        if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
            return true;
        }

        return (
            bytes.length >= 4 &&
            bytes[0] === 0xd0 &&
            bytes[1] === 0xcf &&
            bytes[2] === 0x11 &&
            bytes[3] === 0xe0
        );
    }

    private looksLikeCsvText(text: string): boolean {
        const sample = text.slice(0, 4096).toLowerCase();
        return (
            sample.includes("ean") ||
            sample.includes("gtin") ||
            sample.includes("barcode")
        );
    }

    private looksLikeEanListText(text: string): boolean {
        return /\d{4,}/.test(text.slice(0, 4096));
    }

    private decodeTextBuffer(bytes: Uint8Array): string {
        if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
            return new TextDecoder("utf-16le").decode(bytes).replace(/^\ufeff/, "");
        }

        if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
            return new TextDecoder("utf-16be").decode(bytes).replace(/^\ufeff/, "");
        }

        return new TextDecoder("utf-8").decode(bytes).replace(/^\ufeff/, "");
    }

    private parseCsvText(text: string): CartUploadProduct[] {
        return this.parseProductsFromRows(this.readRowsFromCsvText(text));
    }

    private parseExcelBuffer(bytes: Uint8Array): CartUploadProduct[] {
        const workbook = XLSX.read(bytes, {
            type: "array",
            cellDates: false,
            cellNF: true,
            cellText: true,
        });

        if (!workbook.SheetNames.length) {
            throw new Error("Excel file contains no sheets");
        }

        const errors: string[] = [];

        for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];
            const rowErrors: string[] = [];

            try {
                const rows = this.normalizeRows(this.readExcelRows(sheet));
                return this.parseProductsFromRows(rows);
            } catch (error: any) {
                rowErrors.push(error?.message || "Unknown Excel parse error");

                try {
                    const rows = this.normalizeRows(
                        this.readExcelRowsFromJson(sheet),
                    );
                    return this.parseProductsFromRows(rows);
                } catch (jsonError: any) {
                    errors.push(
                        `${sheetName}: ${rowErrors[0]}; ${jsonError?.message || "JSON fallback failed"}`,
                    );
                }
            }
        }

        throw new Error(
            errors[0] ||
                "Could not read Excel file. Use columns EAN/GTIN and Quantity (or Order).",
        );
    }

    private readRowsFromCsvText(text: string): string[][] {
        const lines = text
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        const rows = lines
            .filter((line) => !/^sep=/i.test(line))
            .map((line) => this.parseCsvLine(line));

        return this.normalizeRows(rows);
    }

    private readRowsFromExcelBuffer(bytes: Uint8Array): string[][] {
        const workbook = this.readExcelWorkbook(bytes);
        const errors: string[] = [];

        for (const sheetName of workbook.SheetNames) {
            try {
                const rows = this.readNormalizedExcelSheet(
                    workbook.Sheets[sheetName],
                );
                if (rows.length) {
                    return rows;
                }
            } catch (error: any) {
                errors.push(
                    `${sheetName}: ${error?.message || "Unknown Excel parse error"}`,
                );
            }
        }

        throw new Error(
            errors[0] ||
                "Could not read Excel file. Use a column named EAN or GTIN.",
        );
    }

    private readEanCodesFromExcelBuffer(bytes: Uint8Array): string[] {
        const workbook = this.readExcelWorkbook(bytes);
        const errors: string[] = [];

        for (const sheetName of workbook.SheetNames) {
            try {
                const rows = this.readNormalizedExcelSheet(
                    workbook.Sheets[sheetName],
                );
                if (!rows.length) {
                    continue;
                }
                return this.parseEanCodesFromRows(rows);
            } catch (error: any) {
                errors.push(
                    `${sheetName}: ${error?.message || "Unknown Excel parse error"}`,
                );
            }
        }

        throw new Error(
            errors[0] ||
                "Could not read Excel file. Use a column named EAN or GTIN.",
        );
    }

    private readExcelWorkbook(bytes: Uint8Array): XLSX.WorkBook {
        const workbook = XLSX.read(bytes, {
            type: "array",
            cellDates: false,
            cellNF: true,
            cellText: true,
        });

        if (!workbook.SheetNames.length) {
            throw new Error("Excel file contains no sheets");
        }

        return workbook;
    }

    private readNormalizedExcelSheet(sheet: XLSX.WorkSheet): string[][] {
        try {
            const rows = this.normalizeRows(this.readExcelRows(sheet));
            if (rows.length) {
                return rows;
            }
        } catch {
            // Fall through to raw JSON values so formatted scientific
            // notation (4.0059E+12) cannot replace the real EAN.
        }

        return this.normalizeRows(this.readExcelRowsFromJson(sheet));
    }

    private readExcelRowsFromJson(sheet: XLSX.WorkSheet): string[][] {
        const rawRows = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(
            sheet,
            {
                header: 1,
                defval: "",
                raw: true,
            },
        );

        return rawRows
            .map((row) =>
                Array.isArray(row)
                    ? row.map((cell) => this.formatJsonCellValue(cell))
                    : [],
            )
            .filter((row) => row.some((cell) => cell !== ""));
    }

    private formatJsonCellValue(cell: string | number | boolean | null): string {
        if (cell == null || cell === "") {
            return "";
        }
        if (typeof cell === "number") {
            return this.formatNumericCellValue(cell);
        }
        if (typeof cell === "boolean") {
            return cell ? "1" : "0";
        }
        return this.sanitizeFormattedText(String(cell));
    }

    private normalizeRows(rows: string[][]): string[][] {
        if (!rows.length) {
            return rows;
        }

        const maxColumns = rows.reduce(
            (max, row) => Math.max(max, row.length),
            0,
        );

        return rows.map((row) => {
            const normalized = [...row];
            while (normalized.length < maxColumns) {
                normalized.push("");
            }
            return normalized.map((cell) => String(cell ?? "").trim());
        });
    }

    private parseCsvLine(line: string): string[] {
        const columns: string[] = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
                continue;
            }

            if ((char === "," || char === "\t" || char === ";") && !inQuotes) {
                columns.push(current.trim());
                current = "";
                continue;
            }

            current += char;
        }

        columns.push(current.trim());
        return columns;
    }

    private readExcelRows(sheet: XLSX.WorkSheet): string[][] {
        const rangeRef = sheet["!ref"];
        if (!rangeRef) {
            return [];
        }

        const range = XLSX.utils.decode_range(rangeRef);
        const rows: string[][] = [];

        for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
            const row: string[] = [];

            for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex++) {
                const cell =
                    sheet[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })];
                row.push(this.formatExcelCellValue(cell));
            }

            if (row.some((cell) => cell !== "")) {
                rows.push(row);
            }
        }

        return rows;
    }

    private formatExcelCellValue(cell?: XLSX.CellObject): string {
        if (!cell) {
            return "";
        }

        // Always prefer the raw number. Excel General format shows 13-digit
        // EANs as 4.0059E+12 in cell.w, which is not a valid barcode.
        if (typeof cell.v === "number" && Number.isFinite(cell.v)) {
            return this.formatNumericCellValue(cell.v);
        }

        if (cell.t === "b") {
            return cell.v ? "1" : "0";
        }

        const raw =
            cell.v != null && String(cell.v).trim() !== ""
                ? String(cell.v).trim()
                : String(cell.w ?? "").trim();

        return this.sanitizeFormattedText(raw);
    }

    private sanitizeFormattedText(value: string): string {
        const trimmed = value.trim().replace(/\u00a0/g, " ");
        if (!trimmed) {
            return "";
        }

        // Do not Number() scientific notation — 4.0059E+12 becomes 4005900000000.
        if (/^\d+(\.\d+)?e[+\-]?\d+$/i.test(trimmed.replace(/\s/g, ""))) {
            return trimmed;
        }

        if (/^[\d,.\s]+$/.test(trimmed)) {
            const withoutCommas = trimmed.replace(/,/g, "");
            if (/^\d+\.0+$/.test(withoutCommas)) {
                return withoutCommas.split(".")[0];
            }
            return withoutCommas;
        }

        return trimmed;
    }

    private formatNumericCellValue(value: number): string {
        if (!Number.isFinite(value)) {
            return "";
        }

        const rounded = Math.round(value);
        if (Math.abs(value - rounded) < 1e-9) {
            return String(rounded);
        }

        return String(value);
    }

    private extractEanCodesFromCell(raw: string): string[] {
        const text = String(raw ?? "").trim();
        if (!text) {
            return [];
        }

        if (/^\d{4,14}$/.test(text)) {
            return [text];
        }

        const compact = text.replace(/\s/g, "");
        if (/^\d+(\.\d+)?e[+\-]?\d+$/i.test(compact)) {
            return [];
        }

        const sanitized = this.sanitizeFormattedText(text);
        if (/^\d{4,14}$/.test(sanitized)) {
            return [sanitized];
        }

        const groupingStripped = text.replace(/[.,\s\u00a0]/g, "");
        if (/^\d{4,14}$/.test(groupingStripped)) {
            return [groupingStripped];
        }

        const codes: string[] = [];
        for (const part of text.split(/[\s,;]+/)) {
            const token = part.trim();
            if (/^\d{4,14}$/.test(token)) {
                codes.push(token);
                continue;
            }
            const tokenDigits = token.replace(/[.,\s\u00a0]/g, "");
            if (/^\d{4,14}$/.test(tokenDigits)) {
                codes.push(tokenDigits);
            }
        }
        return codes;
    }

    private normalizeHeader(header: string): string {
        return header
            .replace(/^\ufeff/, "")
            .replace(/\s+/g, " ")
            .replace(/\*+$/, "")
            .trim()
            .toLowerCase();
    }

    private isGtinHeader(header: string): boolean {
        const normalized = this.normalizeHeader(header);
        if (CartUploadService.GTIN_HEADERS.includes(normalized)) {
            return true;
        }

        return /\b(ean|gtin|barcode|upc)\b/.test(normalized);
    }

    private isQuantityHeader(header: string): boolean {
        const normalized = this.normalizeHeader(header);
        if (CartUploadService.QUANTITY_HEADERS.includes(normalized)) {
            return true;
        }

        return (
            /\b(quantity|qty|amount)\b/.test(normalized) ||
            normalized === "order" ||
            /\border qty\b/.test(normalized) ||
            /\bqty order\b/.test(normalized)
        );
    }

    private isSupplierHeader(header: string): boolean {
        const normalized = this.normalizeHeader(header);
        return (
            CartUploadService.SUPPLIER_HEADERS.includes(normalized) ||
            /\b(supplier|pricelist|offer)\b/.test(normalized)
        );
    }

    private findColumnIndex(
        headers: string[],
        matcher: (header: string) => boolean,
    ): number {
        return headers.findIndex((header) => matcher(header));
    }

    private parseQuantity(value?: string): number {
        if (!value) {
            return NaN;
        }

        const normalized = this.sanitizeFormattedText(value);
        const parsed = Number.parseInt(normalized, 10);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }

        const numeric = Number(normalized);
        return Number.isFinite(numeric) ? Math.floor(numeric) : NaN;
    }

    private normalizeCellGtin(value: string): string {
        let gtin = this.sanitizeFormattedText(
            value.trim().replace(/^\ufeff/, ""),
        );

        return normalizeGtin(gtin);
    }

    private parseProductsFromRows(rows: string[][]): CartUploadProduct[] {
        if (!rows.length) {
            throw new Error("File is empty");
        }

        const headerRowIndex = this.findHeaderRowIndex(rows);
        if (headerRowIndex < 0) {
            throw new Error(
                "No EAN/GTIN column found. Expected a column named EAN or GTIN.",
            );
        }

        const headers = rows[headerRowIndex].map((header) =>
            this.normalizeHeader(header),
        );

        const eanIndex = this.findColumnIndex(headers, (header) =>
            this.isGtinHeader(header),
        );
        const quantityIndex = this.findColumnIndex(headers, (header) =>
            this.isQuantityHeader(header),
        );
        const supplierIdIndex = this.findColumnIndex(headers, (header) =>
            this.isSupplierHeader(header),
        );

        if (eanIndex === -1) {
            throw new Error(
                "No EAN/GTIN column found. Expected a column named EAN or GTIN.",
            );
        }

        if (quantityIndex === -1) {
            throw new Error(
                "No quantity column found. Expected a column named Quantity, Qty, or Order.",
            );
        }

        const products: CartUploadProduct[] = [];

        for (let i = headerRowIndex + 1; i < rows.length; i++) {
            const columns = rows[i];
            const gtin = this.normalizeCellGtin(columns[eanIndex] ?? "");
            const quantity = this.parseQuantity(columns[quantityIndex]);
            const supplierIdStr =
                supplierIdIndex !== -1
                    ? columns[supplierIdIndex]?.trim()
                    : undefined;
            const normalizedOfferId = supplierIdStr
                ? this.normalizeHeader(supplierIdStr)
                : "";
            const consolidatedFeedMarker =
                normalizedOfferId.length > 0 &&
                CartUploadService.CONSOLIDATED_FEED_OFFER_MARKERS.has(
                    normalizedOfferId,
                );
            const parsedSupplierId = supplierIdStr
                ? parseInt(supplierIdStr, 10)
                : undefined;
            const supplierId =
                !consolidatedFeedMarker &&
                parsedSupplierId &&
                !isNaN(parsedSupplierId) &&
                parsedSupplierId > 0
                    ? parsedSupplierId
                    : undefined;

            if (gtin && gtin !== "0" && !isNaN(quantity) && quantity > 0) {
                const product: CartUploadProduct = {
                    gtin,
                    quantity,
                };
                if (supplierId) {
                    product.supplierId = supplierId;
                }
                if (consolidatedFeedMarker) {
                    product.consolidatedFeedMarker = true;
                }
                products.push(product);
            }
        }

        if (products.length === 0) {
            throw new Error(
                "No valid products found. Each row needs an EAN/GTIN and a positive quantity.",
            );
        }

        return products;
    }

    private parseEanCodesFromRows(rows: string[][]): string[] {
        if (!rows.length) {
            throw new Error("File is empty");
        }

        const headerRowIndex = this.findHeaderRowIndex(rows);
        let eanIndex = -1;
        let startRow = 0;

        if (headerRowIndex >= 0) {
            const headers = rows[headerRowIndex].map((header) =>
                this.normalizeHeader(header),
            );
            eanIndex = this.findColumnIndex(headers, (header) =>
                this.isGtinHeader(header),
            );
            if (eanIndex !== -1) {
                startRow = headerRowIndex + 1;
            }
        }

        const eans: string[] = [];
        const seen = new Set<string>();

        const addCode = (raw: string) => {
            for (const code of this.extractEanCodesFromCell(raw)) {
                if (!seen.has(code)) {
                    seen.add(code);
                    eans.push(code);
                }
            }
        };

        for (let i = startRow; i < rows.length; i++) {
            const columns = rows[i];
            if (eanIndex !== -1) {
                addCode(columns[eanIndex] ?? "");
            } else {
                for (const cell of columns) {
                    addCode(cell);
                }
            }
        }

        // Single row like: ean,773602685448,3614273776127
        if (eans.length === 0 && eanIndex !== -1) {
            const headerRow = rows[headerRowIndex];
            for (let c = 0; c < headerRow.length; c++) {
                if (c === eanIndex) {
                    continue;
                }
                addCode(headerRow[c] ?? "");
            }
        }

        if (eans.length === 0) {
            throw new Error(
                "No EAN/GTIN codes found. Use a column named EAN or GTIN, or a list of codes.",
            );
        }

        return eans;
    }

    private findHeaderRowIndex(rows: string[][]): number {
        const rowsToInspect = Math.min(rows.length, 30);

        for (let i = 0; i < rowsToInspect; i++) {
            const headers = rows[i].map((cell) => this.normalizeHeader(cell));
            const hasGtinHeader = headers.some((header) =>
                this.isGtinHeader(header),
            );
            const hasQuantityHeader = headers.some((header) =>
                this.isQuantityHeader(header),
            );

            if (hasGtinHeader && hasQuantityHeader) {
                return i;
            }
        }

        for (let i = 0; i < rowsToInspect; i++) {
            const headers = rows[i].map((cell) => this.normalizeHeader(cell));
            if (headers.some((header) => this.isGtinHeader(header))) {
                return i;
            }
        }

        return -1;
    }

    private validateConsolidatedFeedProducts(
        products: CartUploadProduct[],
        allowedPricingIds?: number[],
    ): void {
        const allowed =
            allowedPricingIds == null
                ? null
                : new Set(allowedPricingIds.map((id) => String(id)));
        const invalidOfferIds = products.filter(
            (product) =>
                product.supplierId != null &&
                allowed != null &&
                !allowed.has(String(product.supplierId)),
        );
        if (invalidOfferIds.length > 0) {
            throw new Error(
                "One or more Offer IDs are not part of this consolidated feed.",
            );
        }
    }

    private async resolveConsolidatedFeedPricingIds(): Promise<number[]> {
        try {
            const result = await this.dataService
                .query<{
                    storefrontConsolidatedFeed: {
                        pricingIds?: Array<string | number> | null;
                    } | null;
                }>(GET_STOREFRONT_CONSOLIDATED_FEED)
                .pipe(take(1))
                .toPromise();
            const ids = (result?.storefrontConsolidatedFeed?.pricingIds ?? [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0);
            if (!ids.length) {
                throw new Error("No consolidated feed channels are available. No products were imported.");
            }
            return ids;
        } catch (error) {
            throw new Error("Could not load consolidated feed channels. Please retry; no products were imported.");
        }
    }

    async addCheapestVariantsToCart(
        products: CartUploadProduct[],
        options: CartUploadOptions = {},
    ): Promise<{
        successfulCount: number;
        failedProductGtins: string[];
        moqViolations: Array<{ gtin: string; moq: number; quantity: number }>;
        stockShortfalls: ImportStockShortfall[];
    }> {
        this.stockShortfalls = [];
        if (products.length === 0) {
            throw new Error("No valid products found in file");
        }

        const pageIsConsolidated = options.consolidatedFeedImport === true ||
            !!options.allowedPricingIds?.length;
        const consolidatedProducts = products.filter(product =>
            pageIsConsolidated || product.consolidatedFeedMarker === true);
        const normalProducts = products.filter(product =>
            !pageIsConsolidated && product.consolidatedFeedMarker !== true);
        const isConsolidatedFeedImport = consolidatedProducts.length > 0;
        // Validate every consolidated row before either group can change the cart.
        if (isConsolidatedFeedImport) {
            const allowedPricingIds = await this.resolveConsolidatedFeedPricingIds();
            this.validateConsolidatedFeedProducts(consolidatedProducts, allowedPricingIds);
        }

        this.notificationService
            .info(`Processing ${products.length} products...`)
            .subscribe();

        // Separate requests preserve the backend's existing per-import mode and line flags.
        // Run sequentially so the second group sees stock already added by the first.
        const results = [];
        if (normalProducts.length) {
            results.push(await this.addAllItemsToCart(normalProducts, false));
        }
        if (consolidatedProducts.length) {
            results.push(await this.addAllItemsToCart(consolidatedProducts, true));
        }
        const result = {
            successfulCount: results.reduce((sum, item) => sum + item.successfulCount, 0),
            failedProductGtins: [...new Set(results.reduce<string[]>((all, item) => all.concat(item.failedProductGtins), []))],
            moqViolations: results.reduce<Array<{ gtin: string; moq: number; quantity: number }>>((all, item) => all.concat(item.moqViolations), []),
            stockShortfalls: results.reduce<ImportStockShortfall[]>((all, item) => all.concat(item.stockShortfalls), []),
        };
        this.stockShortfalls = result.stockShortfalls;
        this.showNotification(
            result.successfulCount,
            result.failedProductGtins,
            result.moqViolations,
        );
        if (isConsolidatedFeedImport) {
            await syncConsolidatedFeedOrderFlagFromActiveOrder(
                this.dataService,
            );
        }
        return result;
    }

    private async addAllItemsToCart(
        products: CartUploadProduct[],
        consolidatedFeedImport = false,
    ): Promise<{
        successfulCount: number;
        failedProductGtins: string[];
        moqViolations: Array<{ gtin: string; moq: number; quantity: number }>;
        stockShortfalls: ImportStockShortfall[];
    }> {
        const items = products.map((product) => ({
            quantity: product.quantity,
            gtin: normalizeGtin(product.gtin),
            supplierId: product.supplierId,
        }));

        const result = await this.dataService
            .mutate<
                {
                    addMultipleItemsToOrder: {
                        order: GetActiveOrderQuery["activeOrder"];
                        failedProductGtins: string[];
                        stockShortfalls: ImportStockShortfall[];
                        moqViolations: Array<{ gtin: string; moq: number; quantity: number }>;
                    };
                },
                {
                    items: {
                        quantity: number;
                        gtin: string;
                        supplierId?: number;
                    }[];
                    consolidatedFeedImport?: boolean;
                }
            >(ADD_MULTIPLE_ITEMS_TO_ORDER, {
                items,
                consolidatedFeedImport: consolidatedFeedImport || undefined,
            })
            .pipe(take(1))
            .toPromise();

        if (!result?.addMultipleItemsToOrder) {
            this.notificationService
                .error("Failed to add products to the cart.")
                .subscribe();
            throw new Error("No response from server");
        }

        const { order, failedProductGtins, moqViolations } =
            result.addMultipleItemsToOrder;
        this.stockShortfalls = result.addMultipleItemsToOrder.stockShortfalls ?? [];
        const failed = new Set((failedProductGtins ?? []).map(normalizeGtin));
        const unavailable = new Set(this.stockShortfalls
            .filter(item => item.importedQuantity === 0).map(item => normalizeGtin(item.gtin)));

        if (order?.id) {
            this.stateService.setState("activeOrderId", order.id);
            this.notificationService
                .info("Loading cart details...")
                .subscribe();
        }

        return {
            successfulCount: new Set(products.map(product => normalizeGtin(product.gtin))
                .filter(gtin => !failed.has(gtin) && !unavailable.has(gtin))).size,
            stockShortfalls: this.stockShortfalls,
            failedProductGtins: failedProductGtins || [],
            moqViolations: moqViolations || [],
        };
    }

    private showNotification(
        successfulCount: number,
        failedProductGtins: string[],
        moqViolations: Array<{ gtin: string; moq: number; quantity: number }> = [],
    ) {
        const failedCount = failedProductGtins?.length || 0;
        const moqFailedCount = moqViolations?.length || 0;

        if (failedCount > 0 || moqFailedCount > 0 || this.stockShortfalls.length > 0) {
            const parts: string[] = [
                `Added ${successfulCount} products to the cart.`,
            ];
            if (failedCount > 0) {
                parts.push(`${failedCount} product(-s) was not found.`);
            }
            if (moqFailedCount > 0) {
                parts.push(
                    `${moqFailedCount} product(-s) did not meet minimum order quantity.`,
                );
            }
            if (this.stockShortfalls.length) {
                const missing = this.stockShortfalls.reduce((sum, item) => sum + item.missingQuantity, 0);
                parts.push(`${missing} units were not imported because they are out of stock.`);
                parts.push(...this.stockShortfalls.slice(0, 5).map(item =>
                    `EAN ${item.gtin}: ${item.importedQuantity} imported, ${item.missingQuantity} not imported.`));
                if (this.stockShortfalls.length > 5) parts.push("Open your cart for the full stock report.");
            }
            this.notificationService.error(parts.join(" ")).subscribe();
        } else {
            this.notificationService
                .success(
                    `Successfully added ${successfulCount} products to the cart`,
                )
                .subscribe();
        }
    }
}
